import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";

const REASON_COPY = {
  not_found: {
    title: "Invite not found.",
    body: "Either the link was mistyped or it never existed. Ask whoever sent it for a fresh one.",
  },
  expired: {
    title: "This invite has expired.",
    body: "onebrain invites are time-limited. Ask whoever sent it to send a fresh one.",
  },
  revoked: {
    title: "This invite was revoked.",
    body: "Looks like the workspace owner pulled this invite back. Ask them for a new one.",
  },
  exhausted: {
    title: "This invite has been used up.",
    body: "Shareable links have a maximum number of uses. Ask the owner for a fresh link.",
  },
};

export default function JoinPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const wsCtx = useWorkspaces();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chatIdParam = searchParams.get("chat") || null;
  // Used to forward ?chat= through the signup/login round-trip for
  // not-yet-authed share-link openers.
  const inviteTokenQS = chatIdParam
    ? `${encodeURIComponent(token)}&chat=${encodeURIComponent(chatIdParam)}`
    : encodeURIComponent(token);

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const autoAcceptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setInfo({ valid: false, reason: "not_found" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isAuthed = user && user.id;
  const isAuthLoading = user === null;

  const acceptInvite = async ({ silent = false } = {}) => {
    if (accepting) return;
    setAccepting(true);
    try {
      const body = chatIdParam ? { chat_id: chatIdParam } : {};
      const { data } = await api.post(`/invites/${token}/accept`, body);
      if (wsCtx?.refetch) await wsCtx.refetch();
      if (!silent) {
        toast.success(
          data?.already_member
            ? `Already a member of ${data.workspace.name}`
            : `Welcome to ${data.workspace.name}`
        );
      }
      const fallbackSlug = data?.workspace?.slug;
      const redirect =
        data?.redirect_to ||
        (fallbackSlug ? `/w/${fallbackSlug}` : "/all-chats");
      navigate(redirect);
    } catch (e) {
      toast.error(formatApiError(e));
      setAccepting(false);
    }
  };

  // Share-link auto-accept: when a chat deep-link is present AND the user is
  // already signed in, skip the confirm step and route straight to the chat.
  // The /accept call is idempotent — works whether they're already a member
  // or being added net-new.
  useEffect(() => {
    if (autoAcceptedRef.current) return;
    if (!isAuthed) return;
    if (!info || !info.valid) return;
    if (!chatIdParam) return;
    if (accepting) return;
    autoAcceptedRef.current = true;
    acceptInvite({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, info, chatIdParam]);

  return (
    <div
      className="min-h-screen w-full bg-[#F3F1EC] flex items-center justify-center px-6 py-10"
      data-testid="join-page"
    >
      <Link
        to="/"
        className="absolute top-8 left-8 font-serif text-xl tracking-tight text-[#2C2A29] select-none hover:opacity-80 transition-opacity"
      >
        <Wordmark size="md" testid="join-header-wordmark" />
      </Link>

      <div className="w-full max-w-md bg-[#FAF9F7] border border-[#DCD7CE] rounded-2xl shadow-sm px-8 py-10 sm:px-10 sm:py-12">
        {loading || isAuthLoading ? (
          <div
            className="flex items-center gap-2 font-sans text-sm text-[#7A7571]"
            data-testid="join-loading"
          >
            <Loader2 className="animate-spin" size={14} /> Looking up your
            invite…
          </div>
        ) : !info || !info.valid ? (
          <InvalidInvite reason={info?.reason} />
        ) : chatIdParam && isAuthed ? (
          <div
            className="flex items-center gap-2 font-sans text-sm text-[#7A7571]"
            data-testid="join-opening-chat"
          >
            <Loader2 className="animate-spin" size={14} /> Opening chat in{" "}
            <span className="font-medium text-[#2C2A29]">
              {info.workspace?.name || "workspace"}
            </span>
            …
          </div>
        ) : (
          <ValidInvite
            info={info}
            isAuthed={Boolean(isAuthed)}
            user={user}
            inviteTokenQS={inviteTokenQS}
            chatIdParam={chatIdParam}
            accepting={accepting}
            onAccept={() => acceptInvite()}
          />
        )}
      </div>
    </div>
  );
}

function InvalidInvite({ reason }) {
  const copy = REASON_COPY[reason] || REASON_COPY.not_found;
  return (
    <div data-testid={`join-invalid-${reason || "not_found"}`}>
      <p className="font-sans text-xs uppercase tracking-[0.22em] text-[#8C3A1F] mb-3">
        Invite trouble
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2A29] mb-4">
        {copy.title}
      </h1>
      <p className="font-sans text-base text-[#7A7571] leading-relaxed mb-8">
        {copy.body}
      </p>
      <Link
        to="/"
        data-testid="join-back-home"
        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium hover:bg-[#1A1918] transition-colors"
      >
        Back to onebrain
        <ArrowRight size={14} strokeWidth={2} />
      </Link>
    </div>
  );
}

function ValidInvite({
  info,
  isAuthed,
  user,
  inviteTokenQS,
  chatIdParam,
  accepting,
  onAccept,
}) {
  const wsName = info.workspace?.name || "a workspace";
  const inviterName = info.inviter?.display_name || "Someone";
  const role = info.role || "member";
  const memberCount = info.workspace?.member_count;

  // Email mismatch warning — only shown when the invite is bound to a
  // specific email AND the signed-in user's email is different.
  const emailMismatch =
    isAuthed &&
    info.invited_email &&
    user?.email &&
    info.invited_email.toLowerCase() !== user.email.toLowerCase();

  // For the unauthed signup/login fallthrough we want to forward the chat
  // deep-link too so the user lands in the right chat after auth.
  const signupHref = chatIdParam
    ? `/signup?invite=${encodeURIComponent(
        info.token || ""
      )}&chat=${encodeURIComponent(chatIdParam)}`
    : `/signup?invite=${inviteTokenQS}`;
  // Note: react-router will re-decode this; the JoinPage URL is preserved
  // verbatim via `next` so we can re-resolve `?chat` after login.
  const loginNext = chatIdParam
    ? `/join/${info.token || ""}?chat=${chatIdParam}`
    : `/join/${info.token || ""}`;
  const loginHref = `/login?next=${encodeURIComponent(loginNext)}`;

  return (
    <div data-testid="join-valid">
      <div className="flex items-center gap-2 mb-3">
        <Users size={14} className="text-[#D96C4A]" strokeWidth={1.8} />
        <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          You're invited
        </span>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2A29] mb-4">
        Join {wsName}
        <span className="text-[#D96C4A]">.</span>
      </h1>
      <p className="font-sans text-base text-[#7A7571] leading-relaxed mb-2">
        <span className="font-medium text-[#2C2A29]">{inviterName}</span>{" "}
        invited you in as{" "}
        <span className="font-medium text-[#2C2A29]">{role}</span>.
      </p>
      {typeof memberCount === "number" ? (
        <p className="font-sans text-xs text-[#A5A29E] mb-8">
          Currently {memberCount} member{memberCount === 1 ? "" : "s"}.
        </p>
      ) : (
        <div className="mb-8" />
      )}

      {emailMismatch ? (
        <div
          className="rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/10 px-4 py-3 mb-5 font-sans text-sm text-[#8C3A1F]"
          data-testid="join-email-mismatch"
        >
          This invite was sent to{" "}
          <strong>{info.invited_email}</strong>, but you're signed in as{" "}
          <strong>{user.email}</strong>. Sign out and sign back in with the
          invited email, or ask for a fresh invite.
        </div>
      ) : null}

      {isAuthed ? (
        <button
          type="button"
          onClick={onAccept}
          disabled={accepting || emailMismatch}
          data-testid="join-accept-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium tracking-wide hover:bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {accepting ? "Joining…" : `Join ${wsName} as ${role}`}
          {!accepting ? <ArrowRight size={14} strokeWidth={2} /> : null}
        </button>
      ) : (
        <div className="space-y-3">
          <Link
            to={signupHref}
            data-testid="join-signup-link"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium tracking-wide hover:bg-[#1A1918] transition"
          >
            Create an account & join
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
          <Link
            to={loginHref}
            data-testid="join-signin-link"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-[#DCD7CE] text-[#2C2A29] font-sans text-sm font-medium tracking-wide hover:bg-[#EFEAE0] transition"
          >
            I already have an account
          </Link>
        </div>
      )}
    </div>
  );
}
